import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { observable } from 'mobx';
import classnames from 'classnames';
import ModalProvider from 'choerodon-ui/pro/lib/modal-provider';
import { iteratorSome } from 'choerodon-ui/pro/lib/_util/iteratorUtils';
import getReactNodeText from 'choerodon-ui/pro/lib/_util/getReactNodeText';
import { TabsPosition, TabsType } from './enum';
import { getDataAttr, getDefaultActiveKey, getDefaultActiveKeyInGroup, getDefaultGroupKey, getHeader, isVertical, normalizePanes } from './utils';
import { Size } from '../_util/enum';
import warning from '../_util/warning';
import TabBar from './TabBar';
import TabContent from './TabContent';
import isFlexSupported from '../_util/isFlexSupported';
import TabsContext from './TabsContext';
import ConfigContext from '../config-provider/ConfigContext';
function handleScroll(e) {
    const { currentTarget } = e;
    const { ownerDocument } = currentTarget;
    if (ownerDocument) {
        const { defaultView } = ownerDocument;
        if (defaultView) {
            const computedStyle = defaultView.getComputedStyle(currentTarget);
            if (computedStyle.overflowX === 'hidden') {
                currentTarget.scrollLeft = 0;
            }
            if (computedStyle.overflowY === 'hidden') {
                currentTarget.scrollTop = 0;
            }
        }
    }
}
function isAnimated(animated) {
    return typeof animated === 'object';
}
const TabsWithContext = function TabsWithContext(props) {
    const { getConfig, getPrefixCls } = useContext(ConfigContext);
    const { tabPosition, className, destroyInactiveTabPane, style, size, type, showMore, tabBarStyle, inkBarStyle = getConfig('tabsInkBarStyle'), hideAdd, animated = true, tabBarGutter, onEdit, tabBarExtraContent, hideOnlyGroup, customizedCode, customizable, children, defaultActiveKey: propDefaultActiveKey, setCustomized, customized, prefixCls: customizePrefixCls, activeKey: propActiveKey, onChange, onTabClick, onPrevClick, onNextClick, keyboard, defaultChangeable, tabDraggable, tabTitleEditable, tabCountHideable, rippleDisabled, flex, ...restProps } = props;
    const hasPropActiveKey = 'activeKey' in props;
    const prefixCls = getPrefixCls('tabs', customizePrefixCls);
    const saveCustomized = useCallback((newCustomized) => {
        if (customizable) {
            setCustomized(newCustomized);
            if (customizedCode) {
                const customizedSave = getConfig('customizedSave');
                customizedSave(customizedCode, newCustomized, 'Tabs');
            }
        }
    }, [customizable, customizedCode]);
    const [totalPanelsMap, groupedPanelsMap] = useMemo(() => normalizePanes(children, customized, {
        tabDraggable,
        tabTitleEditable,
        tabCountHideable,
    }), [children, customized, tabDraggable, tabTitleEditable, tabCountHideable]);
    const defaultActiveKey = useMemo(() => {
        const option = {
            activeKey: propActiveKey,
            defaultActiveKey: propDefaultActiveKey,
        };
        return getDefaultActiveKey(totalPanelsMap, groupedPanelsMap, option);
    }, []);
    const customizedDefaultActiveKey = useMemo(() => {
        if (defaultChangeable && customized) {
            const $defaultActiveKey = customized.defaultActiveKey;
            if ($defaultActiveKey !== undefined) {
                const pane = totalPanelsMap.get($defaultActiveKey);
                if (pane && !pane.disabled) {
                    return $defaultActiveKey;
                }
            }
        }
    }, [defaultChangeable, customized, totalPanelsMap]);
    const actuallyDefaultActiveKey = useMemo(() => {
        if (customizedDefaultActiveKey !== undefined) {
            if (onChange && customizedDefaultActiveKey !== defaultActiveKey) {
                onChange(customizedDefaultActiveKey);
            }
            return customizedDefaultActiveKey;
        }
        return defaultActiveKey;
    }, [defaultActiveKey, customizedDefaultActiveKey]);
    const [activeKey, setActiveKey] = useState(actuallyDefaultActiveKey);
    const activeGroupKey = useMemo(() => {
        if (groupedPanelsMap.size) {
            if (activeKey) {
                let groupKey;
                iteratorSome(groupedPanelsMap.entries(), ([key, { panelsMap }]) => {
                    const found = iteratorSome(panelsMap.keys(), panelKey => panelKey === activeKey);
                    if (found) {
                        groupKey = key;
                    }
                    return found;
                });
                if (groupKey !== undefined) {
                    return groupKey;
                }
            }
            return getDefaultGroupKey(groupedPanelsMap);
        }
    }, [groupedPanelsMap, activeKey]);
    const currentGroup = activeGroupKey ? groupedPanelsMap.get(activeGroupKey) : undefined;
    const currentPanelMap = currentGroup ? currentGroup.panelsMap : totalPanelsMap;
    const refCurrent = {
        activeGroupKey,
        currentGroup,
        currentPanelMap,
    };
    const ref = useRef(refCurrent);
    ref.current = refCurrent;
    const changeActiveKey = useCallback((key, byGroup) => {
        if (activeKey !== key) {
            if (!byGroup && currentGroup) {
                currentGroup.lastActiveKey = key;
            }
            if (!hasPropActiveKey) {
                setActiveKey(key);
            }
            if (onChange) {
                onChange(key);
            }
        }
    }, [hasPropActiveKey, activeKey, onChange, currentGroup]);
    const validationMap = useMemo(() => observable.map(), []);
    const value = {
        defaultActiveKey: propDefaultActiveKey,
        actuallyDefaultActiveKey,
        propActiveKey,
        prefixCls,
        keyboard,
        tabBarPosition: tabPosition,
        hideOnlyGroup,
        customizable,
        customized,
        saveCustomized,
        activeKey,
        activeGroupKey,
        changeActiveKey,
        groupedPanelsMap,
        currentPanelMap,
        totalPanelsMap,
        validationMap,
        onTabClick,
        onPrevClick,
        onNextClick,
        children,
        tabDraggable,
        tabTitleEditable,
        tabCountHideable,
        defaultChangeable,
        rippleDisabled,
    };
    const inkBarAnimated = isAnimated(animated) ? animated.inkBar : animated;
    let tabPaneAnimated = isAnimated(animated) ? animated.tabPane : animated;
    const onTabsChange = getConfig('onTabsChange');
    // card tabs should not have animation
    if (type !== TabsType.line) {
        tabPaneAnimated = 'animated' in props ? tabPaneAnimated : false;
    }
    if (flex) {
        tabPaneAnimated = false;
    }
    const isCard = type === TabsType.card || type === TabsType['editable-card'];
    warning(!(isCard && (size === Size.small || size === Size.large)), 'Tabs[type=card|editable-card] doesn\'t have small or large size, it\'s by designed.');
    const removeTab = useCallback((targetKey, e) => {
        e.stopPropagation();
        if (!targetKey) {
            return;
        }
        if (onEdit) {
            onEdit(targetKey, 'remove');
        }
    }, [onEdit]);
    useEffect(() => {
        if (hasPropActiveKey) {
            if (propActiveKey !== activeKey) {
                setActiveKey(propActiveKey);
            }
        }
        else if (activeKey === undefined || !totalPanelsMap.has(activeKey)) {
            setActiveKey(getDefaultActiveKeyInGroup(currentPanelMap));
        }
    }, [hasPropActiveKey, propActiveKey, activeKey, totalPanelsMap, currentPanelMap]);
    useEffect(() => {
        if (onTabsChange && activeKey) {
            const { current } = ref;
            const currentPanel = current.currentPanelMap.get(activeKey);
            if (currentPanel) {
                const { currentGroup: $currentGroup, activeGroupKey: $activeGroupKey } = current;
                const groupTab = $currentGroup ? $currentGroup.group.tab : undefined;
                const title = getHeader(currentPanel);
                const promises = [getReactNodeText(title)];
                if (groupTab) {
                    promises.push(getReactNodeText(groupTab));
                }
                Promise.all(promises).then(([title, groupTitle]) => {
                    onTabsChange({
                        activeKey,
                        activeGroupKey: $activeGroupKey,
                        title,
                        groupTitle,
                        code: customizedCode,
                    });
                });
            }
        }
    }, [activeKey, onTabsChange, customizedCode]);
    const cls = classnames(prefixCls, `${prefixCls}-${tabPosition}`, `${prefixCls}-${type}`, isVertical(tabPosition) ? `${prefixCls}-vertical` : `${prefixCls}-horizontal`, {
        [`${prefixCls}-${size}`]: !!size,
        [`${prefixCls}-card`]: isCard,
        [`${prefixCls}-flex`]: flex,
        [`${prefixCls}-no-animation`]: !tabPaneAnimated,
        'no-flex': !isFlexSupported(),
    }, className);
    const extraContent = !hideAdd && type === TabsType['editable-card'] ? (
    // Add new tab handler
    React.createElement("div", { key: "tabBarExtraContent", className: `${prefixCls}-extra-bar` }, tabBarExtraContent)) : tabBarExtraContent;
    const tabBarProps = {
        inkBarAnimated,
        extraContent,
        style: tabBarStyle,
        tabBarGutter,
        type,
        showMore,
        onRemoveTab: removeTab,
        onEdit,
        inkBarStyle,
        hideAdd,
    };
    const tabContentProps = {
        animatedWithMargin: true,
        animated: tabPaneAnimated,
        destroyInactiveTabPane,
    };
    const contents = [
        React.createElement(TabBar, Object.assign({ key: "tabBar" }, tabBarProps)),
        React.createElement(TabContent, Object.assign({ key: "tabContent" }, tabContentProps)),
    ];
    if (tabPosition === TabsPosition.bottom) {
        contents.reverse();
    }
    const tabs = (React.createElement("div", Object.assign({ className: cls, style: style, onScrollCapture: tabPaneAnimated ? handleScroll : undefined }, getDataAttr(restProps)), contents));
    return (React.createElement(TabsContext.Provider, { value: value }, customizable ? (React.createElement(ModalProvider, null, tabs)) : tabs));
};
TabsWithContext.displayName = 'TabsWithContext';
export default TabsWithContext;
